import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  adjustOpsProgram,
  TOpsProgramDetailEx,
  TOpsProgramLedger,
} from '../../../requests/opsRegistrationsExtra';
import {
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../ui';
import { LEDGER_LABEL, ledgerTone, signed } from './shared';

/** Every movement of the balance, plus manual adjustments. */
const LedgerCard: React.FC<{
  programId: string;
  program: TOpsProgramDetailEx;
  ledger?: TOpsProgramLedger;
  live: boolean;
}> = ({ programId, program, ledger, live }) => {
  const queryClient = useQueryClient();
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const refreshDetail = () =>
    queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });

  const adjust = useMutation({
    mutationFn: () =>
      adjustOpsProgram(programId, {
        amount: Number(adjustAmount),
        note: adjustNote.trim(),
      }),
    onSuccess: () => {
      showToast('Adjustment recorded', 'success');
      setAdjustAmount('');
      setAdjustNote('');
      refreshDetail();
      queryClient.invalidateQueries({
        queryKey: ['opsProgramLedger', programId],
      });
      queryClient.invalidateQueries({ queryKey: ['opsPrograms'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card
      title={`Ledger · ${ledger?.items.length ?? 0}`}
      className="mt-4"
      right={
        <span className="text-xs text-gray-bold">
          {live
            ? 'Rows that move the balance are marked; rehearsals from before cutover stay for the record.'
            : 'Rehearsals only — nothing here has moved the balance.'}
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-2 border-b border-white-three px-5 py-3">
        <div>
          <div className={labelClass}>Manual adjustment</div>
          <input
            className={`${inputClass} w-36`}
            type="number"
            step="0.01"
            placeholder="Amount"
            value={adjustAmount}
            onChange={(e) => setAdjustAmount(e.target.value)}
          />
        </div>
        <input
          className={`${inputClass} w-80`}
          placeholder="Why — recorded with your name"
          value={adjustNote}
          onChange={(e) => setAdjustNote(e.target.value)}
        />
        <button
          className={btnSecondary}
          disabled={
            adjust.isPending || !adjustNote.trim() || !Number(adjustAmount)
          }
          onClick={() => adjust.mutate()}
        >
          {adjust.isPending ? 'Recording…' : 'Record adjustment'}
        </button>
        <div className="basis-full text-xs text-gray-bold">
          Positive uses funds, negative credits them back.
          {program.teamPass && ' On a Team Pass it moves the tally.'}
          {!live && ' Recorded as a rehearsal until the ledger is live.'}
        </div>
      </div>
      {ledger && ledger.items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className={`${tableClass} min-w-[900px]`}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Date</th>
                <th className={thClass}>Type</th>
                <th className={thClass}>Registration / class</th>
                <th className={thClass}>Note</th>
                <th className={thClass}>By</th>
                <th className={`${thClass} text-right`}>Amount</th>
                {program.teamPass && (
                  <th className={`${thClass} text-right`}>Value consumed</th>
                )}
              </tr>
            </thead>
            <tbody>
              {ledger.items.map((row) => (
                <tr key={row.id}>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    {row.at?.slice(0, 10)}
                  </td>
                  <td className={tdClass}>
                    <Pill
                      value={LEDGER_LABEL[row.type] ?? row.type}
                      tone={ledgerTone(row)}
                    />
                  </td>
                  <td className={tdClass}>
                    <div className="font-medium text-gray-four">
                      {row.kind === 'event'
                        ? row.className ?? '—'
                        : row.learner ?? '—'}
                    </div>
                    {row.kind === 'registration' && row.className && (
                      <div className="text-xs text-gray-bold">
                        {row.className}
                      </div>
                    )}
                    {row.kind === 'event' && (
                      <div className="text-xs text-gray-bold">
                        private event
                      </div>
                    )}
                  </td>
                  <td className={`${tdClass} max-w-xs text-xs`}>
                    {row.note ?? '—'}
                    {row.flags.length > 0 && (
                      <div className="text-gray-bold">
                        {row.flags.join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className={`${tdClass} text-xs`}>{row.actor ?? '—'}</td>
                  <td
                    className={`${tdClass} whitespace-nowrap text-right ${
                      row.live ? '' : 'text-gray-bold'
                    }`}
                  >
                    {signed(row.amount)}
                  </td>
                  {program.teamPass && (
                    <td className={`${tdClass} whitespace-nowrap text-right`}>
                      {signed(row.valueConsumed)}
                    </td>
                  )}
                </tr>
              ))}
              <tr>
                <td className={tdClass} colSpan={5}>
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                    {live ? 'Net of live rows' : 'Would deduct in total'}
                  </span>
                </td>
                <td
                  className={`${tdClass} whitespace-nowrap text-right font-bold text-gray-four`}
                >
                  {signed(live ? ledger.liveAmount : ledger.totalAmount)}
                </td>
                {program.teamPass && (
                  <td
                    className={`${tdClass} whitespace-nowrap text-right font-bold text-gray-four`}
                  >
                    {signed(ledger.liveValueConsumed)}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 py-6 text-sm text-gray-bold">
          No ledger rows yet. Seats added to this program from a class roster
          appear here.
        </div>
      )}
    </Card>
  );
};

export default LedgerCard;
